import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel, Divider,
  CircularProgress, Chip, IconButton, InputAdornment, Tooltip, Stepper,
  Step, StepLabel, StepContent, Badge, Alert, Collapse, Card, CardContent,
  Avatar, LinearProgress
} from '@mui/material';
import {
  CloudUpload, PictureAsPdf, Description, Image as ImageIcon, InsertDriveFile,
  Search, FilterList, Refresh, CheckCircle, Cancel, Send, KeyboardReturn,
  Visibility, Close, AttachFile, History, FolderOpen, ExpandMore, ExpandLess,
  Download, Article
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5002';

const FORM_TYPES = [
  'KYC Update',
  'New Investor Registration',
  'Investment Form',
  'Bank Details Update',
  'Compliance Form',
  'Other'
];

const STATUS_CONFIG = {
  PENDING_APPROVAL:      { label: 'Pending Approval',      color: '#ff9800', bg: '#fff3e0', icon: '⏳' },
  UNDER_CHECKER_REVIEW:  { label: 'Under Checker Review',  color: '#2196f3', bg: '#e3f2fd', icon: '🔍' },
  APPROVED_BY_CHECKER:   { label: 'Approved by Checker',   color: '#009688', bg: '#e0f2f1', icon: '✅' },
  SENT_BACK_BY_CHECKER:  { label: 'Sent Back by Checker',  color: '#2196f3', bg: '#e3f2fd', icon: '↩️' },
  APPROVED:              { label: 'Approved',               color: '#4caf50', bg: '#e8f5e9', icon: '✅' },
  REJECTED:              { label: 'Rejected',               color: '#f44336', bg: '#ffebee', icon: '❌' },
  SENT_BACK:             { label: 'Sent Back',              color: '#2196f3', bg: '#e3f2fd', icon: '↩️' },
};

const WORKFLOW_STEPS = [
  { label: 'Submitted by Maker',    actions: ['SUBMITTED'] },
  { label: 'Checker Review',        actions: ['APPROVED_BY_CHECKER','SENT_BACK_BY_CHECKER'] },
  { label: 'Admin Final Approval',  actions: ['APPROVED_BY_ADMIN','REJECTED_BY_ADMIN','SENT_BACK_BY_ADMIN'] },
];

const ACTION_COLORS = {
  SUBMITTED:              '#1a3c6e',
  SENT_TO_CHECKER:        '#1976d2',
  APPROVED_BY_CHECKER:    '#009688',
  SENT_BACK_BY_CHECKER:   '#ff9800',
  APPROVED_BY_ADMIN:      '#4caf50',
  REJECTED_BY_ADMIN:      '#f44336',
  SENT_BACK_BY_ADMIN:     '#2196f3',
};

const getFileIcon = (name = '') => {
  const l = name.toLowerCase();
  if (l.endsWith('.pdf')) return <PictureAsPdf sx={{ color: '#f44336', fontSize: 20 }} />;
  if (l.match(/\.(doc|docx)$/)) return <Description sx={{ color: '#1976d2', fontSize: 20 }} />;
  if (l.match(/\.(jpg|jpeg|png|gif|webp)$/)) return <ImageIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
  return <InsertDriveFile sx={{ color: '#607d8b', fontSize: 20 }} />;
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#607d8b', bg: '#eceff1' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg, color: cfg.color, fontWeight: 700,
        border: `1px solid ${cfg.color}33`, fontSize: '0.72rem', px: 0.5
      }}
    />
  );
};

const FilePreviewRow = ({ file, label }) => {
  if (!file) return null;
  const getUrl = (f) => {
    const pathStr = (f.path || '').replace(/\\/g, '/');
    const idx = pathStr.indexOf('/uploads/');
    const rel = idx !== -1 ? pathStr.substring(idx) : `/uploads/general/${pathStr.split('/').pop()}`;
    return `${API_BASE}${rel}`;
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, borderRadius: 1.5,
      border: '1px solid #e8edf5', bgcolor: '#f8faff', '&:hover': { bgcolor: '#eef2ff' } }}>
      {getFileIcon(file.name)}
      <Box flex={1} minWidth={0}>
        <Typography fontSize={13} fontWeight={600} noWrap>{file.name}</Typography>
        <Typography fontSize={11} color="text.secondary">{label} • {(file.size / 1024).toFixed(1)} KB</Typography>
      </Box>
      <Tooltip title="View / Download">
        <IconButton size="small" onClick={() => window.open(getUrl(file), '_blank')}
          sx={{ bgcolor: '#1a3c6e', color: '#fff', '&:hover': { bgcolor: '#0f2444' }, borderRadius: 1 }}>
          <Download fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const DropZone = ({ label, helpText, multiple, files, onFiles, accept = '*' }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    onFiles(multiple ? dropped : dropped.slice(0, 1));
  }, [multiple, onFiles]);

  const handleChange = (e) => {
    const picked = Array.from(e.target.files);
    onFiles(multiple ? picked : picked.slice(0, 1));
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => inputRef.current?.click()}
      sx={{
        border: `2px dashed ${dragging ? '#1a3c6e' : '#c5cfe8'}`,
        borderRadius: 2, p: 2.5, cursor: 'pointer', textAlign: 'center',
        bgcolor: dragging ? '#e8edf5' : '#f8faff',
        transition: 'all 0.2s',
        '&:hover': { borderColor: '#1a3c6e', bgcolor: '#e8edf5' }
      }}
    >
      <input ref={inputRef} type="file" hidden multiple={multiple} accept={accept} onChange={handleChange} />
      <CloudUpload sx={{ fontSize: 32, color: dragging ? '#1a3c6e' : '#94a3b8', mb: 0.5 }} />
      <Typography fontSize={13} fontWeight={600} color={dragging ? '#1a3c6e' : 'text.primary'}>
        {files && files.length > 0
          ? multiple
            ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
            : files[0].name
          : 'Click to upload or drag & drop'}
      </Typography>
      <Typography fontSize={11} color="text.secondary" mt={0.3}>{helpText}</Typography>
      {files && files.length > 0 && multiple && (
        <Box mt={1} display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
          {files.map((f, i) => (
            <Chip key={i} label={f.name} size="small" icon={getFileIcon(f.name)}
              sx={{ fontSize: 10, maxWidth: 120 }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function FormUploadDirectoryPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const isMaker   = user?.role === 'MAKER';
  const isChecker = user?.role === 'CHECKER';
  const isAdmin   = user?.role === 'ADMIN';

  // ── Form state ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({ formType: '', formPurpose: '' });
  const [formFile, setFormFile] = useState([]);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(isMaker || isAdmin);

  // ── Table state ──────────────────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const LIMIT = 10;

  // ── Dialog state ─────────────────────────────────────────────────────────
  const [openRecord, setOpenRecord] = useState(null);
  const [actionDialog, setActionDialog] = useState(null); // { type, record }
  const [actionRemark, setActionRemark] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);

  // ── Next FRM display ──────────────────────────────────────────────────────
  const nextNum = records.length > 0
    ? parseInt((records.find(r => r.refNo)?.refNo || 'FRM0000000000').replace('FRM', ''), 10) + 1
    : 1;
  const nextRef = 'FRM' + String(nextNum).padStart(10, '0');
  const previewRef = records.length > 0 ? 'FRM' + String(nextNum - 1).padStart(10, '0') : 'FRM0000000001';

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/form-uploads', {
        params: { search, status: statusFilter, page, limit: LIMIT }
      });
      setRecords(data.records);
      setPagination(data.pagination);
    } catch { } finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  useEffect(() => {
    const handler = (e) => {
      const updated = e.detail?.record;
      if (!updated) return;
      setRecords(prev => {
        const exists = prev.find(r => r._id === updated._id);
        return exists ? prev.map(r => r._id === updated._id ? updated : r) : [updated, ...prev];
      });
    };
    window.addEventListener('form_upload_update', handler);
    return () => window.removeEventListener('form_upload_update', handler);
  }, []);

  const handleReset = () => {
    setFormData({ formType: '', formPurpose: '' });
    setFormFile([]);
    setSupportingDocs([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.formType || !formData.formPurpose) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' }); return;
    }
    if (formFile.length === 0) {
      enqueueSnackbar('Please upload the Form file', { variant: 'warning' }); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('formType', formData.formType);
      fd.append('formPurpose', formData.formPurpose);
      fd.append('formFile', formFile[0]);
      supportingDocs.forEach(f => fd.append('supportingDocs', f));

      const { data } = await api.post('/form-uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRecords(prev => [data, ...prev]);
      handleReset();
      enqueueSnackbar(`Form ${data.refNo} submitted for approval!`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Upload failed', { variant: 'error' });
    } finally { setSubmitting(false); }
  };

  const handleWorkflowAction = async () => {
    const { type, record } = actionDialog;
    const needsRemark = ['checker-send-back', 'admin-reject', 'admin-send-back'].includes(type);
    if (needsRemark && !actionRemark.trim()) {
      enqueueSnackbar('Please provide a remark', { variant: 'warning' }); return;
    }
    setActionLoading(true);
    try {
      const { data } = await api.post(`/form-uploads/${record._id}/${type}`, { remark: actionRemark });
      enqueueSnackbar(data.message, { variant: 'success' });
      setRecords(prev => prev.map(r => r._id === data.record._id ? data.record : r));
      if (openRecord?._id === data.record._id) setOpenRecord(data.record);
      setActionDialog(null);
      setActionRemark('');
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Action failed', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const currentStep = (status) => {
    if (['PENDING_APPROVAL'].includes(status)) return 0;
    if (['APPROVED_BY_CHECKER', 'SENT_BACK_BY_CHECKER'].includes(status)) return 1;
    if (['APPROVED', 'REJECTED', 'SENT_BACK'].includes(status)) return 2;
    return 0;
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 1, md: 2 }, py: 2 }}>

      {/* ── PAGE HEADER ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a3c6e" letterSpacing={-0.5}>
            Form Upload Directory
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            Manage, upload and track forms through the approval workflow
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRecords} sx={{ border: '1px solid #e0e7ef' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          {(isMaker || isAdmin) && (
            <Button
              variant={showForm ? 'contained' : 'outlined'}
              startIcon={<Article />}
              onClick={() => setShowForm(p => !p)}
              sx={{ bgcolor: showForm ? '#1a3c6e' : undefined, '&:hover': { bgcolor: showForm ? '#0f2444' : undefined } }}
            >
              {showForm ? 'Hide Form' : 'Upload New Form'}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── UPLOAD FORM ──────────────────────────────────────────────────── */}
      <Collapse in={showForm}>
        <Paper elevation={0} sx={{
          mb: 3, p: { xs: 2.5, md: 4 },
          border: '1px solid #dde3f0', borderRadius: 3,
          background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)'
        }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={3} pb={2} sx={{ borderBottom: '1px solid #eef2ff' }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#1a3c6e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FolderOpen sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#1a3c6e">Upload New Form</Typography>
              <Typography variant="caption" color="text.secondary">Fill in the details and upload required documents</Typography>
            </Box>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>

              {/* Row 1: Form Type + Form Purpose */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Form Type *</InputLabel>
                  <Select
                    label="Form Type *"
                    value={formData.formType}
                    onChange={e => setFormData(p => ({ ...p, formType: e.target.value }))}
                  >
                    {FORM_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth size="small" label="Form Purpose *" required
                  placeholder="Enter Form Purpose"
                  value={formData.formPurpose}
                  onChange={e => setFormData(p => ({ ...p, formPurpose: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth size="small" label="Form Reference No"
                  value={nextRef}
                  disabled
                  helperText={`Next: ${nextRef} (Auto Generated)`}
                  sx={{ '& .MuiInputBase-root': { bgcolor: '#f0f4ff', fontWeight: 700 } }}
                />
              </Grid>

              {/* Row 2: Form Upload */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" fontWeight={700} color="#1a3c6e" textTransform="uppercase" letterSpacing={0.5} mb={0.8} display="block">
                  Form Upload *
                </Typography>
                <DropZone
                  label="Form File"
                  helpText="PDF / DOC / Any extension supported"
                  multiple={false}
                  files={formFile}
                  onFiles={setFormFile}
                />
              </Grid>

              {/* Row 2: Supporting Docs */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" fontWeight={700} color="#1a3c6e" textTransform="uppercase" letterSpacing={0.5} mb={0.8} display="block">
                  Supporting Documents (Optional)
                </Typography>
                <DropZone
                  label="Supporting Docs"
                  helpText="Supporting documents upload होंगे। (Multiple files allowed)"
                  multiple={true}
                  files={supportingDocs}
                  onFiles={setSupportingDocs}
                />
              </Grid>

              {/* Workflow info */}
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#e8edf5', borderRadius: 2, p: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Send sx={{ color: '#1a3c6e', fontSize: 18 }} />
                  <Typography fontSize={12} color="#1a3c6e" fontWeight={600}>
                    After submit → Status: <b>Pending Approval</b> &nbsp;|&nbsp; Workflow: Maker → Checker → Admin
                  </Typography>
                </Box>
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
                  <Button variant="outlined" onClick={() => setShowForm(false)} sx={{ px: 3 }}>Cancel</Button>
                  <Button variant="outlined" color="inherit" onClick={handleReset} sx={{ px: 3, color: '#555', borderColor: '#ccc' }}>Reset</Button>
                  <Button
                    type="submit" variant="contained" disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send />}
                    sx={{ px: 4, fontWeight: 700, bgcolor: '#1a3c6e', '&:hover': { bgcolor: '#0f2444' } }}
                  >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Collapse>

      {/* ── DATA DISPLAY SECTION ─────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: '1px solid #dde3f0', borderRadius: 3, overflow: 'hidden' }}>

        {/* Section Header */}
        <Box sx={{
          px: 3, py: 2, bgcolor: '#1a3c6e',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <History sx={{ color: '#fff', fontSize: 22 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#fff">Data Display Section</Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.7)">
                {pagination.total} record{pagination.total !== 1 ? 's' : ''} found
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            <TextField
              size="small" placeholder="Search ref, type, purpose..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} /></InputAdornment>,
                sx: { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', borderRadius: 2,
                  '& input': { color: '#fff', '&::placeholder': { color: 'rgba(255,255,255,0.6)' } },
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } }
              }}
              sx={{ width: { xs: '100%', sm: 240 } }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                displayEmpty value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', borderRadius: 2,
                  '& .MuiSvgIcon-root': { color: '#fff' },
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Loading bar */}
        {loading && <LinearProgress sx={{ height: 3 }} />}

        {/* TABLE */}
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Form Reference No', 'Form Type', 'Form Purpose', 'Uploaded Date', 'Uploaded By', 'Approval Status', 'Action'].map(h => (
                  <TableCell key={h} sx={{
                    fontWeight: 700, fontSize: 12, bgcolor: '#f0f4ff', color: '#1a3c6e',
                    textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
                    borderBottom: '2px solid #dde3f0'
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <FolderOpen sx={{ fontSize: 40, color: '#c5cfe8', mb: 1, display: 'block', mx: 'auto' }} />
                    No records found
                  </TableCell>
                </TableRow>
              )}
              {records.map((row, idx) => (
                <TableRow key={row._id} hover sx={{
                  bgcolor: idx % 2 === 0 ? '#fff' : '#fafcff',
                  '&:hover': { bgcolor: '#eef2ff' }
                }}>
                  <TableCell>
                    <Typography fontFamily="monospace" fontWeight={700} fontSize={13} color="#1a3c6e">
                      {row.refNo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.formType} size="small" sx={{ bgcolor: '#e8edf5', fontSize: 11, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13} noWrap sx={{ maxWidth: 180 }} title={row.formPurpose}>
                      {row.formPurpose}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                    {row.uploadedDate}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.8}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: '#1a3c6e' }}>
                        {row.uploadedBy?.name?.[0] || '?'}
                      </Avatar>
                      <Box>
                        <Typography fontSize={12} fontWeight={600} lineHeight={1.2}>{row.uploadedBy?.name || 'Unknown'}</Typography>
                        <Typography fontSize={10} color="text.secondary">{row.uploadedBy?.role}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><StatusBadge status={row.approvalStatus} /></TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Open / View Details">
                        <Button
                          size="small" variant="outlined"
                          startIcon={<Visibility fontSize="small" />}
                          onClick={() => { setOpenRecord(row); setShowHistory(true); }}
                          sx={{ fontSize: 11, px: 1.5, borderColor: '#1a3c6e', color: '#1a3c6e',
                            '&:hover': { bgcolor: '#e8edf5' }, textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                          Open
                        </Button>
                      </Tooltip>
                      {/* Checker actions */}
                      {isChecker && row.approvalStatus === 'PENDING_APPROVAL' && (
                        <>
                          <Tooltip title="Approve → Send to Admin">
                            <IconButton size="small" color="success"
                              onClick={() => setActionDialog({ type: 'checker-approve', record: row })}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send Back to Maker">
                            <IconButton size="small" sx={{ color: '#ff9800' }}
                              onClick={() => setActionDialog({ type: 'checker-send-back', record: row })}>
                              <KeyboardReturn fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Admin actions */}
                      {isAdmin && row.approvalStatus === 'APPROVED_BY_CHECKER' && (
                        <>
                          <Tooltip title="Final Approve">
                            <IconButton size="small" color="success"
                              onClick={() => setActionDialog({ type: 'admin-approve', record: row })}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error"
                              onClick={() => setActionDialog({ type: 'admin-reject', record: row })}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send Back">
                            <IconButton size="small" sx={{ color: '#2196f3' }}
                              onClick={() => setActionDialog({ type: 'admin-send-back', record: row })}>
                              <KeyboardReturn fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" fontSize={12}>
            Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, pagination.total)} of {pagination.total} records
          </Typography>
          <Box display="flex" gap={1}>
            <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              sx={{ fontSize: 11, minWidth: 60 }}>Prev</Button>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, border: '1px solid #dde3f0', borderRadius: 1 }}>
              <Typography fontSize={12} fontWeight={700}>{page} / {pagination.pages || 1}</Typography>
            </Box>
            <Button size="small" variant="outlined" disabled={page >= (pagination.pages || 1)} onClick={() => setPage(p => p + 1)}
              sx={{ fontSize: 11, minWidth: 60 }}>Next</Button>
          </Box>
        </Box>
      </Paper>

      {/* ══════════════════════════════════════════════════════════════════════
          OPEN / VIEW DETAILS DIALOG
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={!!openRecord} onClose={() => setOpenRecord(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

        {openRecord && (
          <>
            {/* Dialog Header */}
            <Box sx={{ bgcolor: '#1a3c6e', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={800} color="#fff" fontFamily="monospace">
                  {openRecord.refNo}
                </Typography>
                <Typography variant="caption" color="rgba(255,255,255,0.7)">
                  {openRecord.formType} • {openRecord.uploadedDate}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1.5}>
                <StatusBadge status={openRecord.approvalStatus} />
                <IconButton onClick={() => setOpenRecord(null)} sx={{ color: '#fff' }}><Close /></IconButton>
              </Box>
            </Box>

            {/* Workflow Progress Stepper */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
              <Stepper activeStep={currentStep(openRecord.approvalStatus)} alternativeLabel>
                {WORKFLOW_STEPS.map((step, i) => (
                  <Step key={i}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Divider />

            <DialogContent sx={{ px: 3, py: 2.5 }}>
              <Grid container spacing={3}>

                {/* LEFT: Form Details + Files */}
                <Grid item xs={12} md={7}>
                  {/* Form Info */}
                  <Typography variant="caption" fontWeight={800} color="#1a3c6e" textTransform="uppercase" letterSpacing={0.8}>
                    Form Details
                  </Typography>
                  <Card elevation={0} sx={{ mt: 1, mb: 2.5, border: '1px solid #dde3f0', borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <Grid container spacing={1.5}>
                        {[
                          ['Form Reference No', openRecord.refNo, true],
                          ['Form Type', openRecord.formType],
                          ['Form Purpose', openRecord.formPurpose],
                          ['Uploaded Date', openRecord.uploadedDate],
                          ['Uploaded By', `${openRecord.uploadedBy?.name || '—'} (${openRecord.uploadedBy?.role || '—'})`],
                        ].map(([label, val, mono]) => (
                          <Grid item xs={12} sm={6} key={label}>
                            <Typography fontSize={10} color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
                              {label}
                            </Typography>
                            <Typography fontSize={13} fontWeight={600} fontFamily={mono ? 'monospace' : 'inherit'} color={mono ? '#1a3c6e' : 'text.primary'}>
                              {val || '—'}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Files */}
                  <Typography variant="caption" fontWeight={800} color="#1a3c6e" textTransform="uppercase" letterSpacing={0.8}>
                    Uploaded Files
                  </Typography>
                  <Box mt={1}>
                    <FilePreviewRow file={openRecord.formFile} label="Main Form" />
                    {(openRecord.supportingDocs || []).map((doc, i) => (
                      <FilePreviewRow key={i} file={doc} label={`Supporting Doc ${i + 1}`} />
                    ))}
                    {!openRecord.formFile && (!openRecord.supportingDocs || openRecord.supportingDocs.length === 0) && (
                      <Typography fontSize={13} color="text.secondary" py={1}>No files attached.</Typography>
                    )}
                  </Box>
                </Grid>

                {/* RIGHT: Audit History */}
                <Grid item xs={12} md={5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" fontWeight={800} color="#1a3c6e" textTransform="uppercase" letterSpacing={0.8}>
                      Approval History ({(openRecord.history || []).length})
                    </Typography>
                    <IconButton size="small" onClick={() => setShowHistory(p => !p)}>
                      {showHistory ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </Box>
                  <Collapse in={showHistory}>
                    <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
                      {(openRecord.history || []).length === 0 ? (
                        <Typography fontSize={13} color="text.secondary">No history yet.</Typography>
                      ) : (
                        [...(openRecord.history || [])].reverse().map((h, i) => (
                          <Box key={i} sx={{
                            mb: 1.5, p: 1.5, borderRadius: 2,
                            border: `1px solid ${ACTION_COLORS[h.action] || '#dde3f0'}33`,
                            bgcolor: `${ACTION_COLORS[h.action] || '#607d8b'}08`,
                            borderLeft: `3px solid ${ACTION_COLORS[h.action] || '#607d8b'}`
                          }}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Avatar sx={{ width: 22, height: 22, fontSize: 9, bgcolor: ACTION_COLORS[h.action] || '#607d8b' }}>
                                {(h.byUserName || h.byUser?.name || '?')[0]}
                              </Avatar>
                              <Box flex={1}>
                                <Typography fontSize={11} fontWeight={700}>
                                  {h.byUserName || h.byUser?.name || 'Unknown'}
                                  <Chip label={h.byUserRole || h.byUser?.role} size="small"
                                    sx={{ ml: 0.5, fontSize: 9, height: 16, bgcolor: '#eef2ff' }} />
                                </Typography>
                                <Typography fontSize={10} color="text.secondary">
                                  {new Date(h.at).toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={h.action.replace(/_/g, ' ')} size="small"
                              sx={{ fontSize: 10, fontWeight: 700, mb: 0.5,
                                bgcolor: `${ACTION_COLORS[h.action]}20`, color: ACTION_COLORS[h.action] }}
                            />
                            {h.remark && (
                              <Typography fontSize={12} color="text.secondary" mt={0.3} sx={{ fontStyle: 'italic' }}>
                                "{h.remark}"
                              </Typography>
                            )}
                          </Box>
                        ))
                      )}
                    </Box>
                  </Collapse>
                </Grid>
              </Grid>
            </DialogContent>

            {/* Dialog Action Buttons */}
            <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #eef2ff', display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end', bgcolor: '#fafcff' }}>
              {isChecker && openRecord.approvalStatus === 'PENDING_APPROVAL' && (
                <>
                  <Button variant="contained" color="success" startIcon={<CheckCircle />}
                    onClick={() => setActionDialog({ type: 'checker-approve', record: openRecord })}>
                    Approve → Send to Admin
                  </Button>
                  <Button variant="outlined" startIcon={<KeyboardReturn />} sx={{ color: '#ff9800', borderColor: '#ff9800' }}
                    onClick={() => setActionDialog({ type: 'checker-send-back', record: openRecord })}>
                    Send Back to Maker
                  </Button>
                </>
              )}
              {isAdmin && openRecord.approvalStatus === 'APPROVED_BY_CHECKER' && (
                <>
                  <Button variant="contained" color="success" startIcon={<CheckCircle />}
                    onClick={() => setActionDialog({ type: 'admin-approve', record: openRecord })}>
                    Final Approve
                  </Button>
                  <Button variant="contained" color="error" startIcon={<Cancel />}
                    onClick={() => setActionDialog({ type: 'admin-reject', record: openRecord })}>
                    Reject
                  </Button>
                  <Button variant="outlined" startIcon={<KeyboardReturn />} sx={{ color: '#2196f3', borderColor: '#2196f3' }}
                    onClick={() => setActionDialog({ type: 'admin-send-back', record: openRecord })}>
                    Send Back
                  </Button>
                </>
              )}
              <Button variant="outlined" onClick={() => setOpenRecord(null)} sx={{ color: '#555', borderColor: '#ccc' }}>
                Close
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      {/* ── ACTION CONFIRMATION DIALOG ───────────────────────────────────── */}
      <Dialog open={!!actionDialog} onClose={() => { setActionDialog(null); setActionRemark(''); }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {actionDialog && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: '#1a3c6e', pb: 1 }}>
              {{
                'checker-approve':   '✅ Approve & Send to Admin',
                'checker-send-back': '↩️ Send Back to Maker',
                'admin-approve':     '✅ Final Approval',
                'admin-reject':      '❌ Reject Form',
                'admin-send-back':   '↩️ Send Back to Maker',
              }[actionDialog.type]}
            </DialogTitle>
            <DialogContent>
              <Alert severity={
                actionDialog.type.includes('approve') ? 'success' :
                actionDialog.type.includes('reject') ? 'error' : 'warning'
              } sx={{ mb: 2, borderRadius: 2 }}>
                <Typography fontFamily="monospace" fontWeight={700} component="span">
                  {actionDialog.record?.refNo}
                </Typography>
                {' — '}{actionDialog.record?.formType}
              </Alert>
              <TextField
                fullWidth multiline rows={3}
                label={['checker-send-back','admin-reject','admin-send-back'].includes(actionDialog.type) ? 'Remark (Required)' : 'Remark (Optional)'}
                placeholder="Add a comment or remark..."
                value={actionRemark}
                onChange={e => setActionRemark(e.target.value)}
                required={['checker-send-back','admin-reject','admin-send-back'].includes(actionDialog.type)}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => { setActionDialog(null); setActionRemark(''); }}
                sx={{ color: '#555' }}>Cancel</Button>
              <Button
                variant="contained" onClick={handleWorkflowAction} disabled={actionLoading}
                color={actionDialog.type.includes('reject') ? 'error' : actionDialog.type.includes('approve') ? 'success' : 'primary'}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
